/**
 * Route inference: build route structure (DAG) from packing lists and event dates.
 * Used for multi-stop trip planning and optimal quantity calculation.
 */

/** Event-like shape needed for route inference (id, dates, name for messages) */
export interface EventLike {
    id: string;
    name?: string;
    startDate?: Date | null;
    endDate?: Date | null;
}

/** Packing list item with itemType and category for trailer detection */
export interface PackingListItemLike {
    itemType?: {
        id: string;
        category?: { name: string } | null;
    } | null;
}

/** Trailer (item type) with id for path building */
export interface TrailerLike {
    id: string;
}

/** Single segment on a trailer's path: either a storage location (source/dest) or an event */
export type PathSegment =
    | { kind: 'storage'; storageLocationId: string; startDate: Date; endDate: Date }
    | { kind: 'event'; eventId: string; startDate: Date; endDate: Date };

/** Stable node id for graph position/lookup: eventId or storage:storageLocationId */
export function getSegmentNodeId(seg: PathSegment): string {
    return seg.kind === 'storage' ? `storage:${seg.storageLocationId}` : seg.eventId;
}

/** One trailer's ordered path through storage and events */
export interface TrailerPath {
    trailerId: string;
    segments: PathSegment[];
}

/** Date conflict for one trailer */
export interface RouteConflict {
    trailerId: string;
    conflictingEvents: string[];
    reason: string;
}

/** Validation warning or error */
export interface RouteWarning {
    message: string;
    severity: 'info' | 'warning' | 'error';
}

/** Inferred route structure with paths, conflicts, and warnings */
export interface RouteStructure {
    trailerPaths: Map<string, TrailerPath>;
    conflicts: RouteConflict[];
    warnings: RouteWarning[];
}

const MS_PER_DAY = 86400000;

function formatDate(d: Date): string {
    return d.toISOString().slice(0, 10);
}

/**
 * Get trailers (item types with category "Trailers") that appear on an event's packing list.
 */
export function getTrailersForEvent(eventId: string, packingLists: Map<string, PackingListItemLike[]>): TrailerLike[] {
    const items = packingLists.get(eventId) || [];
    const seen = new Set<string>();
    const trailers: TrailerLike[] = [];

    for (const item of items) {
        const it = item.itemType;
        if (!it?.id) continue;
        const categoryName = it.category?.name ?? '';
        if (categoryName === 'Trailers' && !seen.has(it.id)) {
            seen.add(it.id);
            trailers.push({ id: it.id });
        }
    }

    return trailers;
}

/**
 * Infer route structure from operation parameters.
 *
 * Given source events and target events, build the route DAG by analyzing which
 * trailers go where and when. Detects date conflicts.
 *
 * @param sourceStorageLocationIds - When non-empty, a "storage" (e.g. HQ) segment is prepended/appended to paths
 */
export function inferRouteStructure(
    sources: string[],
    targets: string[],
    events: Map<string, EventLike>,
    packingLists: Map<string, PackingListItemLike[]>,
    sourceStorageLocationIds: string[],
    targetStorageLocationIds: string[]
): RouteStructure {
    const trailerPaths = new Map<string, TrailerPath>();
    const conflicts: RouteConflict[] = [];
    const warnings: RouteWarning[] = [];

    const allEventIds = [...targets];
    const allEvents = allEventIds.map(id => events.get(id)).filter((e): e is EventLike => e != null);

    for (const event of allEvents) {
        const trailers = getTrailersForEvent(event.id, packingLists);
        const startDate = event.startDate ?? new Date(0);
        const endDate = event.endDate ?? event.startDate ?? new Date(0);

        for (const trailer of trailers) {
            let path = trailerPaths.get(trailer.id);
            if (!path) {
                path = { trailerId: trailer.id, segments: [] };
                trailerPaths.set(trailer.id, path);
            }
            path.segments.push({
                kind: 'event',
                eventId: event.id,
                startDate,
                endDate,
            });
        }
    }

    for (const path of Array.from(trailerPaths.values())) {
        path.segments.sort((a: PathSegment, b: PathSegment) => a.startDate.getTime() - b.startDate.getTime());
    }

    for (const [trailerId, path] of Array.from(trailerPaths.entries())) {
        for (let i = 0; i < path.segments.length - 1; i++) {
            const current = path.segments[i];
            const next = path.segments[i + 1];
            if (current.kind !== 'event' || next.kind !== 'event') continue;

            const event1 = events.get(current.eventId);
            const event2 = events.get(next.eventId);
            const hasDates1 = event1 && event1.startDate != null && event1.endDate != null;
            const hasDates2 = event2 && event2.startDate != null && event2.endDate != null;
            if (!hasDates1 || !hasDates2) continue;

            if (current.endDate.getTime() >= next.startDate.getTime()) {
                const name1 = event1?.name ?? current.eventId;
                const name2 = event2?.name ?? next.eventId;
                conflicts.push({
                    trailerId,
                    conflictingEvents: [current.eventId, next.eventId],
                    reason: `Trailer assigned to overlapping events: ${name1} (${formatDate(current.startDate)}-${formatDate(current.endDate)}) and ${name2} (${formatDate(next.startDate)}-${formatDate(next.endDate)})`,
                });
            }
        }
    }

    const hasStorageAsSource = sourceStorageLocationIds.length > 0;
    const hasStorageAsTarget = targetStorageLocationIds.length > 0;
    const firstSourceId = sourceStorageLocationIds[0];
    const firstTargetId = targetStorageLocationIds[0];
    if (hasStorageAsSource || hasStorageAsTarget) {
        for (const path of Array.from(trailerPaths.values())) {
            if (hasStorageAsSource && firstSourceId && path.segments.length > 0) {
                const first = path.segments[0];
                path.segments.unshift({
                    kind: 'storage',
                    storageLocationId: firstSourceId,
                    startDate: new Date(first.startDate.getTime() - MS_PER_DAY),
                    endDate: first.startDate,
                });
            }
            if (hasStorageAsTarget && firstTargetId && path.segments.length > 0) {
                const last = path.segments[path.segments.length - 1];
                path.segments.push({
                    kind: 'storage',
                    storageLocationId: firstTargetId,
                    startDate: last.endDate,
                    endDate: new Date(last.endDate.getTime() + MS_PER_DAY),
                });
            }
        }
    }

    return {
        trailerPaths,
        conflicts,
        warnings,
    };
}
